<template>
    <div class="content">
        <base-header class="pb-6">
            <div class="row align-items-center py-4">
                <div class="col-lg-6 col-7">
                    <h6 class="h2 text-white d-inline-block mb-0">
                        {{ $t("menu_pref.title") }}
                    </h6>
                    <nav
                        aria-label="breadcrumb"
                        class="d-none d-md-inline-block ml-md-4"
                    >
                        <route-bread-crumb></route-bread-crumb>
                    </nav>
                </div>
                <div class="col-lg-6 col-5 text-right">
                    <base-button
                        size="md"
                        type="neutral"
                        :disabled="saving || loading"
                        @click="save()"
                        >{{ $t("save") }}</base-button
                    >
                </div>
            </div>
        </base-header>

        <div class="container-fluid mt--6">
            <card class="no-border-card" body-classes="px-0 pb-1" footer-classes="pb-2">
                <template slot="header">
                    <div class="row align-items-center">
                        <div class="col">
                            <h3 class="mb-0">{{ $t("menu_pref.list_title") }}</h3>
                            <p class="text-sm text-muted mb-0">
                                {{ $t("menu_pref.description") }}
                            </p>
                            <p class="text-xs text-muted mb-0">
                                <i class="fa fa-info-circle"></i>
                                {{ $t("menu_pref.note_root_only") }}
                            </p>
                        </div>
                    </div>
                </template>

                <div v-loading="loading">
                    <table class="table align-items-center table-flush">
                        <thead class="thead-light">
                            <tr>
                                <th scope="col">{{ $t("menu_pref.col_menu") }}</th>
                                <th scope="col" class="text-center" style="width: 140px">
                                    {{ $t("menu_pref.col_show") }}
                                </th>
                                <th scope="col" class="text-center" style="width: 140px">
                                    {{ $t("menu_pref.col_pin") }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="row in rows" :key="row.key">
                                <td>
                                    <span :style="{ paddingLeft: row.level * 24 + 'px' }">
                                        <span
                                            :class="{
                                                'font-weight-bold': row.level === 0
                                            }"
                                            >{{ label(row) }}</span
                                        >
                                    </span>
                                </td>
                                <td class="text-center">
                                    <el-checkbox
                                        v-if="row.level === 0"
                                        :value="isShown(row.key)"
                                        :disabled="loading || isPinned(row.key)"
                                        @input="setShown(row.key, $event)"
                                    ></el-checkbox>
                                    <span v-else class="text-muted">&mdash;</span>
                                </td>
                                <td class="text-center">
                                    <el-checkbox
                                        v-if="row.level === 0"
                                        :value="isPinned(row.key)"
                                        :disabled="loading"
                                        @input="setPinned(row.key, $event)"
                                    ></el-checkbox>
                                    <span v-else class="text-muted">&mdash;</span>
                                </td>
                            </tr>
                            <tr v-if="rows.length === 0 && !loading">
                                <td colspan="3" class="text-center text-muted">
                                    {{ $t("menu_pref.empty") }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <template slot="footer">
                    <div class="text-right">
                        <base-button
                            type="primary"
                            :disabled="saving || loading"
                            @click="save()"
                            >{{ $t("save") }}</base-button
                        >
                    </div>
                </template>
            </card>
        </div>
    </div>
</template>
<script>
import { mapGetters, mapActions } from "vuex";
import { Checkbox, Loading } from "element-ui";
import swal from "sweetalert2";

export default {
    name: "MenuSettings",
    components: {
        [Checkbox.name]: Checkbox
    },
    directives: {
        loading: Loading.directive
    },
    data() {
        return {
            loading: false,
            saving: false,
            // key -> { shown: boolean, pinned: boolean }
            model: {}
        };
    },
    computed: {
        ...mapGetters({
            visibleTree: "menuPref/visibleTree",
            hiddenMenus: "menuPref/hiddenMenus",
            pinnedMenus: "menuPref/pinnedMenus"
        }),
        // Flattened rows (parents + children) with a level for indentation.
        rows() {
            const out = [];
            const walk = (nodes, level) => {
                nodes.forEach(n => {
                    out.push({ key: n.key, i18n: n.i18n, level });
                    if (n.children && n.children.length) {
                        walk(n.children, level + 1);
                    }
                });
            };
            walk(this.visibleTree, 0);
            return out;
        }
    },
    methods: {
        ...mapActions({
            loadPrefs: "menuPref/load",
            savePrefs: "menuPref/save"
        }),
        label(row) {
            return this.$t(row.i18n);
        },
        // Safe accessors: never throw even if `model` hasn't been built yet
        // (e.g. first render tick, before created()/fetch() populate it) or
        // for a key that isn't in the model (child rows are display-only and
        // are never given a model entry). Default = shown, not pinned.
        isShown(key) {
            const m = this.model[key];
            return m ? m.shown : true;
        },
        isPinned(key) {
            const m = this.model[key];
            return m ? m.pinned : false;
        },
        setShown(key, val) {
            if (!this.model[key]) {
                this.$set(this.model, key, { shown: true, pinned: false });
            }
            this.model[key].shown = val;
        },
        // Pin implies visible: pinning forces "shown" on and locks it.
        setPinned(key, val) {
            if (!this.model[key]) {
                this.$set(this.model, key, { shown: true, pinned: false });
            }
            this.model[key].pinned = val;
            if (val) {
                this.model[key].shown = true;
            }
        },
        // Only top-level (root) menus are togglable — hiding/pinning applies
        // per root menu only; child rows are rendered display-only (#133 only
        // wired 23 root-level v-if gates in the sidebar so far).
        buildModel() {
            const hidden = this.hiddenMenus || [];
            const pinned = this.pinnedMenus || [];
            const model = {};
            this.rows.forEach(row => {
                if (row.level !== 0) return;
                const isPinnedRow = pinned.indexOf(row.key) !== -1;
                model[row.key] = {
                    shown: isPinnedRow ? true : hidden.indexOf(row.key) === -1,
                    pinned: isPinnedRow
                };
            });
            this.model = model;
        },
        // Race guard: buildModel() below REPLACES `model` wholesale once the
        // GET resolves. If the user could interact with the checkboxes while
        // this request is in flight, a click made just before the response
        // lands gets silently clobbered by the rebuild. All checkboxes (and
        // both Save buttons) are `:disabled` while `loading` is true, so
        // there is no window for that to happen.
        async fetch() {
            this.loading = true;
            try {
                await this.loadPrefs();
            } catch (e) {
                // fail-open handled in the store; still render defaults
            } finally {
                this.buildModel();
                this.loading = false;
            }
        },
        async save() {
            const hidden = [];
            const pinned = [];
            this.rows.forEach(row => {
                if (row.level !== 0) return;
                const m = this.model[row.key];
                if (!m) return;
                if (m.pinned) {
                    pinned.push(row.key);
                } else if (!m.shown) {
                    hidden.push(row.key);
                }
            });
            this.saving = true;
            try {
                await this.savePrefs({ hidden, pinned });
                swal.fire({
                    title: this.$t("menu_pref.saved"),
                    icon: "success",
                    timer: 1500,
                    showConfirmButton: false
                });
            } catch (e) {
                swal.fire({
                    title: this.$t("menu_pref.save_failed"),
                    icon: "error"
                });
            } finally {
                this.saving = false;
            }
        }
    },
    created() {
        // Build a default model synchronously (before first render) so the
        // template never reads an undefined `model[row.key]` — fetch() below
        // rebuilds it once the real hidden/pinned prefs come back.
        this.buildModel();
    },
    mounted() {
        this.fetch();
    }
};
</script>
